import { AbstractDialog } from "./abstract_dialog.js";
import { DialogManager } from "./dialog_manager.js";
import { RoundedRectangle } from "../common_ui_elements/rounded_rectangle.js";
import { UITextInput } from "../common_ui_elements/ui_text_input.js";
import { setButtonTextOutline } from "../common_ui_elements/button_text_outline.js";
import { ColorScheme } from "../common_ui_elements/color_scheme.js";
import { ImgID } from "../../image_loader/img_id.js";
import { UITextElement, MyTextAlign, MyTextVerticalAlign } from "../common_ui_elements/ui_text_element.js";
import { TextStyleScheme } from "../common_ui_elements/text_style_scheme.js";
import { updatePlayer } from "../../api/requests/player.requests.js";
import { StatisticsStorage } from "../../storage/statistics_storage.js";
import { LeaderboardSyncService } from "../../api/leaderboard/leaderboard_sync_service.js";
import { UINineSliceElement } from "../common_ui_elements/ui_nine_slice_element.js";
import { UIImage } from "../common_ui_elements/ui_image.js";

const CONTENT_BG_Y_DESIGN = 145;
const CONTENT_BG_H_DESIGN = 165;

export class EditNameDialog extends AbstractDialog
{
  private confirm_button!: UIImage;
  private confirm_text!: UITextElement;
  private name_input!: UITextInput;
  private readonly initial_name: string;
  private readonly onConfirm: ( name: string ) => void;

  constructor( initial_name: string, onConfirm: ( name: string ) => void )
  {
    const design_w = 850;
    const design_h = 550;

    const w = LayoutScaler.widthFromDesign( design_w );
    const h = LayoutScaler.heightFromDesign( design_h );
    const x = ( DialogManager.getWidth()  - w ) / 2;
    const y = ( DialogManager.getHeight() - h ) / 2;

    super( DialogManager.ctx, x, y, w, h );

    this.initial_name = initial_name;
    this.onConfirm    = onConfirm;

    this.createBackground();
    this.createCloseButton();
    this.createNameInput();
    this.createConfirmButton();
  }

  private createBackground(): void
  {
    const design_banner_w     = 730;
    const design_banner_h     = CONTENT_BG_H_DESIGN;
    const design_banner_y     = CONTENT_BG_Y_DESIGN;

    const bg = new UINineSliceElement(
      this.ctx,
      0,
      0,
      this.rect.getW(),
      this.rect.getH(),
      ImgID.UI_CapsuleBlue,
      "capsule_bg"
    );

    this.addChild( bg );

    const banner_w = LayoutScaler.widthFromDesign( design_banner_w );
    const banner_h = LayoutScaler.heightFromDesign( design_banner_h );
    const banner_x = ( this.rect.getW() - banner_w ) / 2;
    const banner_y = LayoutScaler.yFromDesign( design_banner_y );

    const substrate = new RoundedRectangle(
      this.ctx,
      banner_x,
      banner_y,
      banner_w,
      banner_h,
      LayoutScaler.cornerRadiusFromDesign( 24 ),
      "#153791",
      undefined,
      false,
      undefined,
      0,
      {
        color: "rgba(255, 255, 255, 0.25)",
        offsetX: 0,
        offsetY: LayoutScaler.yFromDesign( 3 ),
        blur: LayoutScaler.widthFromDesign( 6 )
      },
      {
        color: "rgba(0, 0, 0, 0.31)",
        offsetX: 0,
        offsetY: LayoutScaler.yFromDesign( 3 ),
        blur: LayoutScaler.widthFromDesign( 6 )
      }
    );
    this.addChild( substrate );
  }

  private createCloseButton(): void
  {
    const btn_size = LayoutScaler.widthFromDesign( 117 );
    const btn_x = this.rect.getW() - btn_size + LayoutScaler.widthFromDesign( 10 );
    const btn_y = -LayoutScaler.yFromDesign( 100 );

    const close_btn = new UIImage(
      this.ctx,
      btn_x,
      btn_y,
      btn_size,
      btn_size,
      ImgID.UI_SettingsDialogCloseBtn,
      {},
      true
    );

    close_btn.setOnClick( () => this.close() );
    this.addChild( close_btn );
  }

  private createNameInput(): void
  {
    const design_label_h      = 145;
    const design_label_text_size = 80;
    const design_input_w      = 672;
    const design_input_h      = 89;
    const design_input_radius = 16;
    const design_input_text_size = 45;

    const label_h = LayoutScaler.heightFromDesign( design_label_h );
    const label_y = 0;

    const label = new UITextElement(
      this.ctx,
      0,
      label_y,
      this.rect.getW(),
      label_h,
      {
        localization_key: "PROFILE_DIALOG.CHANGE_NAME",
        font_size: LayoutScaler.textSizeFromDesign( design_label_text_size ),
        font_weight: 900,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        text_style: TextStyleScheme.geologica,
        text_color: ColorScheme.white,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 14 ),
        outline_color: ColorScheme.settings_dialog_title_outline,
        shadow_blur: LayoutScaler.widthFromDesign( 5 ),
        shadow_offset_x: LayoutScaler.xFromDesign( 0 ),
        shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
      }
    );
    label.setClickable( false );
    this.addChild( label );

    const input_w = LayoutScaler.widthFromDesign( design_input_w );
    const input_h = LayoutScaler.heightFromDesign( design_input_h );
    const input_x = ( this.rect.getW() - input_w ) / 2;
    const content_y = LayoutScaler.yFromDesign( CONTENT_BG_Y_DESIGN );
    const content_h = LayoutScaler.heightFromDesign( CONTENT_BG_H_DESIGN );
    const input_y = content_y + ( content_h - input_h ) / 2;

    this.name_input = new UITextInput(
      this.ctx,
      input_x,
      input_y,
      input_w,
      input_h,
      {
        initial_value: this.initial_name,
        max_length: 10,
        style: {
          text_style:    TextStyleScheme.geologica,
          text_align:    MyTextAlign.Center,
          background_color: ColorScheme.white,
          text_color:    ColorScheme.black,
          font_size:     LayoutScaler.textSizeFromDesign( design_input_text_size ),
          font_weight:   800,
          padding:       0,
          text_offset_y: LayoutScaler.yFromDesign( 5 ),
          border_radius: LayoutScaler.cornerRadiusFromDesign( design_input_radius ),
          border_width:  0,
        },
      }
    );

    this.name_input.setOnClick( () => this.name_input.focus() );
    this.name_input.onEnterPressed  = () => { this.name_input.blur(); };
    this.name_input.onEscapePressed = () => { this.name_input.blur(); };
    this.name_input.onValueChanged  = () =>
    {
      const val = this.name_input.getValue().trim();
      if ( val.length > 0 && val !== this.initial_name )
        this.activeConfirmButton();
      else
        this.inactiveConfirmButton();
    };

    this.addChild( this.name_input );
  }

  private createConfirmButton(): void
  {
    const design_button_w   = 512;
    const design_button_h   = 169;
    const design_button_pad_y = 45;
    const design_text_size  = 54;

    const button_w = LayoutScaler.widthFromDesign( design_button_w );
    const button_h = LayoutScaler.heightFromDesign( design_button_h );
    const button_y = this.rect.getH() - button_h
      - LayoutScaler.heightFromDesign( design_button_pad_y );
    const button_x = ( this.rect.getW() - button_w ) / 2;

    this.confirm_button = new UIImage(
      this.ctx,
      button_x,
      button_y,
      button_w,
      button_h,
      ImgID.UI_AvatarSelectInactiveBtn,
      {},
      true
    );
    this.confirm_button.setClickable( false );

    const confirm_text = new UITextElement(
      this.ctx,
      0,
      0,
      button_w,
      button_h,
      {
        localization_key: "CONFIRM",
        font_size: LayoutScaler.textSizeFromDesign( design_text_size ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        text_style: TextStyleScheme.geologica,
        ...TextStyleScheme.buttonShadow(),
      }
    );

    this.confirm_button.addChild( confirm_text, true );
    this.confirm_text = confirm_text;
    setButtonTextOutline( [ confirm_text ], false, ColorScheme.more_lives_buy_outline );

    this.confirm_button.setOnClick( () =>
    {
      const name = this.name_input.getValue().trim();
      this.onConfirm( name );
      updatePlayer( { nickname: name } ).catch( e => console.warn( '[EditName] sync failed', e ) );
      const stats = StatisticsStorage.load();
      const avatar = parseInt( localStorage.getItem( 'player_avatar_id' ) ?? '0', 10 );
      const fb_url = localStorage.getItem( 'player_facebook_avatar_url' ) ?? undefined;
      void LeaderboardSyncService.syncProfile( stats.level, name, avatar, fb_url );
      this.close();
    } );

    this.addChild( this.confirm_button );
  }

  private inactiveConfirmButton(): void
  {
    this.confirm_button.setClickable( false );
    this.confirm_button.setImageId( ImgID.UI_AvatarSelectInactiveBtn );
    setButtonTextOutline( [ this.confirm_text ], false, ColorScheme.more_lives_buy_outline );
    this.invalidateSubtree();
  }

  private activeConfirmButton(): void
  {
    this.confirm_button.setClickable( true );
    this.confirm_button.setImageId( ImgID.UI_AvatarSelectActiveBtn );
    setButtonTextOutline( [ this.confirm_text ], true, ColorScheme.more_lives_buy_outline );
    this.invalidateSubtree();
  }

  public override onDialogWillClose(): void
  {
    this.name_input.blur();
    this.name_input.cleanup();
  }

  public name(): string
  {
    return "EditNameDialog";
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;
    this.startRender();
    this.finishRender();
  }
}
